// 업적 갤러리(/collection) 조립 — 순수 함수 (2026-09-12)
//
// 배경: /collection은 claude.ai/design A그룹 시안을 그대로 옮긴 정적 목업이라, "23 / 60"·가짜 획득 타일
//   18종이 모든 사용자에게 똑같이 떴다. "황소 60종"에 대응하는 테이블은 없지만, 이 화면의 원래 짝인
//   웹 app/collection("황소 컬렉션")은 `achievements`(정의) + `user_achievements`(획득)로 그려진다.
//   토스트도 업적 해금 시 /collection으로 보낸다("업적 갤러리 = 컬렉션 탭"). → 같은 원천으로 재구성.
//
// 이 파일은 DB 행 → 화면 모델 변환만 한다. 조회는 data-access/achievementsSupabase.ts.

/** achievements 테이블에서 쓰는 컬럼 */
export type AchievementDef = {
  code: string;
  name: string;
  description?: string | null;
  category?: string | null;
  rarity?: string | null;
  hidden?: boolean | null;
  sort_order?: number | null;
};

/** 화면 레어도 4단 — 시안 범례(일반·레어·에픽·전설)와 1:1 */
export type GalleryRarity = "common" | "rare" | "epic" | "legend";

export const RARITY_ORDER: GalleryRarity[] = ["common", "rare", "epic", "legend"];

export const RARITY_LABELS: Record<GalleryRarity, string> = {
  common: "일반",
  rare: "레어",
  epic: "에픽",
  legend: "전설",
};

/**
 * 은퇴한 게임 시스템(2026-06-14 _archive)의 업적 — 지금은 딸 방법이 없다.
 * 목록·분모에서 빼되, **이미 딴 사람에게는 기록으로 남긴다**(buildAchievementGallery 참고).
 *
 * ⚠ prefix가 아니라 코드 명시 목록이다. 웹 CollectionClient의 GAME_PREFIXES는 "hot_"로 묶어서
 *   게임과 무관한 인기글 업적 `hot_post`까지 게임으로 분류한다. macro_*는 거시 이벤트방(/macro-event)이
 *   살아 있어 일단 목록에서 뺐다(아래 보류 참고).
 * 판정 보류(검증 필요, 2026-09-12): tab_explorer(설명의 탭 목록이 웹 기준 "분석·블로그") ·
 *   first_portfolio("가상 포트폴리오 개설") · macro_*("D-1 매크로 예측"이 /macro-event 투표와 같은지).
 */
export const RETIRED_ACHIEVEMENT_CODES: ReadonlySet<string> = new Set([
  // 차트 게임 — first_chart는 카테고리가 onboarding이지만 설명이 "차트 게임 첫 문제 풀이"다(09-12 설명 전수 대조로 추가)
  "chart_daily_30", "chart_daily_7", "chart_mode1_500", "chart_mode1_100", "chart_streak_10", "chart_first_correct", "first_chart",
  // 트랙 레코드
  "tr_bronze", "tr_silver", "tr_gold", "tr_diamond",
  // BTC 더블다운
  "btc_3rounds", "btc_5rounds", "btc_8rounds",
  // 토너먼트
  "tour_winner", "tour_3wins",
  // 마이크로 예측
  "micro_first", "micro_10",
  // 핫 티커
  "hot_first", "hot_3hits",
  // 워들
  "wordle_first", "wordle_7", "wordle_30",
  // OX 퀴즈
  "ox_streak_5", "ox_streak_15",
  // 게임 모음
  "game_collector", "game_master",
]);

/**
 * 카테고리 → 필터칩 라벨·아이콘(CSIcon 이름). 표에 없는 카테고리는 '기타'.
 * ⚠ 키 순서 = 칩 순서 = 잠긴 타일 순서(쉬운 것 → 어려운 것). DB sort_order는 수집가·인기인 같은
 *   어려운 업적이 맨 앞이라, 신규 사용자가 먼저 딸 '첫걸음'이 맨 뒤로 밀렸다(2026-09-12 실화면 확인).
 */
export const CATEGORY_META: Record<string, { label: string; icon: string }> = {
  onboarding: { label: "첫걸음", icon: "compass" },
  activity: { label: "활동", icon: "flame" },
  social: { label: "인기", icon: "heart" },
  milestone: { label: "레벨", icon: "star" },
  finance: { label: "예측", icon: "target" },
  collection: { label: "수집", icon: "trophy" },
  hidden: { label: "히든", icon: "moon" },
};
const OTHER_CATEGORY = { label: "기타", icon: "medal" };

export function categoryMeta(category: string | null | undefined) {
  return (category && CATEGORY_META[category]) || OTHER_CATEGORY;
}

/**
 * 설명 시트에 분류 라벨을 붙일지 — 이름과 분류가 같으면 뺀다(2026-09-12 효신님 결정).
 * 예: 업적 "첫걸음"(분류 onboarding = "첫걸음")이 "첫걸음 · 첫걸음"으로 두 번 보였다.
 * 히든 업적은 잠긴 동안 이름이 "???"라 겹칠 일이 없으므로 표시 이름(displayName)으로 비교한다.
 */
export function showCategoryLabel(displayName: string, category: string | null | undefined): boolean {
  return displayName.trim() !== categoryMeta(category).label;
}

const CATEGORY_RANK = new Map(Object.keys(CATEGORY_META).map((k, i) => [k, i]));
/** 카테고리 정렬 순위 — 표에 없는 카테고리는 맨 뒤 */
function categoryRank(category: string): number {
  return CATEGORY_RANK.get(category) ?? Number.MAX_SAFE_INTEGER;
}

/**
 * DB 레어도 → 화면 4단.
 * 현행 값은 common/rare/epic/legendary. 구버전(bronze~platinum)은 웹 RARITY_COLORS의 대응을 따른다.
 */
export function normalizeRarity(rarity: string | null | undefined): GalleryRarity {
  switch (rarity) {
    case "legendary":
    case "legend":
    case "gold":
      return "legend";
    case "epic":
    case "platinum":
      return "epic";
    case "rare":
    case "silver":
      return "rare";
    default:
      return "common";
  }
}

/**
 * 이름 앞에 박힌 이모지 제거 — 예: "📊 첫 D-1 적중" → "첫 D-1 적중".
 * 앱은 이모지 대신 CSIcon을 쓴다(이모지→아이콘 전환 방침). DB 원문은 건드리지 않고 표시만 다듬는다.
 */
export function stripLeadingEmoji(name: string): string {
  const out = name.replace(/^[\p{Extended_Pictographic}\u200d\ufe0f\s]+/u, "");
  return out || name; // 전부 이모지였다면 원문 유지(빈 이름 방지)
}

/** ISO 시각 → KST 날짜 "YYYY.MM.DD" (획득일 표시) */
export function formatKstDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  const kst = new Date(t + 9 * 60 * 60 * 1000); // UTC+9 — getUTC*로 읽어 기기 시간대와 무관하게
  const mm = String(kst.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(kst.getUTCDate()).padStart(2, "0");
  return `${kst.getUTCFullYear()}.${mm}.${dd}`;
}

export type GalleryItem = {
  code: string;
  name: string; // 이모지 제거본
  description: string | null;
  category: string;
  rarity: GalleryRarity;
  unlockedAt: string | null; // ISO — null이면 잠김
  /** 잠긴 동안 이름을 가릴지 — 히든 카테고리·hidden 플래그 */
  secret: boolean;
};

export type GalleryChip = { key: string; label: string; total: number; got: number };

export type AchievementGallery = {
  items: GalleryItem[]; // 획득(최근순) → 잠김(카테고리 순위 → sort_order순)
  total: number;
  got: number;
  locked: number;
  pct: number; // 0~100 정수
  rarityTotals: Record<GalleryRarity, number>;
  chips: GalleryChip[]; // 카테고리 칩(항목이 있는 것만, CATEGORY_META 순)
};

/**
 * 정의 + 내 획득 맵 → 갤러리 모델.
 * @param defs      achievements 행 (sort_order 정렬 가정 — 칩 순서가 이 순서를 따른다)
 * @param unlocked  achievement_code → unlocked_at(ISO)
 */
export function buildAchievementGallery(
  defs: AchievementDef[],
  unlocked: Record<string, string>,
): AchievementGallery {
  const items: GalleryItem[] = [];
  for (const d of defs) {
    const unlockedAt = unlocked[d.code] ?? null;
    // 은퇴 업적은 딴 사람에게만 보인다 — 못 따는 잠김 타일은 좌절감만 준다.
    if (RETIRED_ACHIEVEMENT_CODES.has(d.code) && !unlockedAt) continue;
    const category = d.category || "other";
    items.push({
      code: d.code,
      name: stripLeadingEmoji(d.name || d.code),
      description: d.description ?? null,
      category,
      rarity: normalizeRarity(d.rarity),
      unlockedAt,
      secret: category === "hidden" || d.hidden === true,
    });
  }

  // 칩: 항목이 있는 카테고리만 모은 뒤 CATEGORY_META 순서로 정렬
  const chipMap = new Map<string, GalleryChip>();
  const rarityTotals: Record<GalleryRarity, number> = { common: 0, rare: 0, epic: 0, legend: 0 };
  let got = 0;
  for (const it of items) {
    if (!chipMap.has(it.category)) {
      chipMap.set(it.category, { key: it.category, label: categoryMeta(it.category).label, total: 0, got: 0 });
    }
    const chip = chipMap.get(it.category)!;
    chip.total += 1;
    rarityTotals[it.rarity] += 1;
    if (it.unlockedAt) {
      chip.got += 1;
      got += 1;
    }
  }

  // 정렬: 획득한 것 먼저(최근 획득순) → 잠긴 것(카테고리 순위 → 같은 카테고리 안에선 defs 순서).
  const sorted = items
    .map((it, idx) => ({ it, idx }))
    .sort((a, b) => {
      const ua = a.it.unlockedAt, ub = b.it.unlockedAt;
      if (ua && ub) return ub.localeCompare(ua) || a.idx - b.idx;
      if (ua) return -1;
      if (ub) return 1;
      return categoryRank(a.it.category) - categoryRank(b.it.category) || a.idx - b.idx;
    })
    .map((x) => x.it);

  const total = items.length;
  return {
    items: sorted,
    total,
    got,
    locked: total - got,
    pct: total > 0 ? Math.round((got / total) * 100) : 0,
    rarityTotals,
    chips: [...chipMap.values()].sort((a, b) => categoryRank(a.key) - categoryRank(b.key)),
  };
}

/** 필터칩 판정 — 'all' 전체 · 'raretier' 일반 제외 · 그 외 카테고리 일치 (시안 필터 로직 유지) */
export function matchesGalleryFilter(item: GalleryItem, filter: string): boolean {
  if (filter === "all") return true;
  if (filter === "raretier") return item.rarity !== "common";
  return item.category === filter;
}
